/**
 * The legacy tables that still hold rows and are not being moved.
 *
 * Grouped by REASON rather than listed by size, and that is the whole design of
 * this card. A flat list of table names with big numbers beside them reads as a
 * hole in the import - as work somebody forgot - when most of it is the
 * opposite: our own business register mirror is larger and fresher than the old
 * CRM's frozen 2022 copy, so moving that copy across would make our data older,
 * not more complete. The heading states the decision, the paragraph argues it,
 * and the tables sit underneath as evidence.
 *
 * `uncovered` is the one group that IS a hole, and it keeps its own wording for
 * exactly that reason: a decision and an omission must never look alike.
 */
import { PackageOpen } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { SkippedTable } from "../../../../lib/crmImport";
import { Card, CardHead, fmtNum } from "../../../ui";
import { reasonLabel, reasonWhy } from "./labels";

/** Reasons in the order they appear, each with its tables. Insertion order is
 *  the server's order, which is largest first. */
function group(rows: SkippedTable[]): { reason: string; tables: SkippedTable[]; rows: number }[] {
  const out = new Map<string, { reason: string; tables: SkippedTable[]; rows: number }>();
  for (const row of rows) {
    const bucket = out.get(row.reason) ?? { reason: row.reason, tables: [], rows: 0 };
    bucket.tables.push(row);
    bucket.rows += row.rows;
    out.set(row.reason, bucket);
  }
  return [...out.values()];
}

export function SkippedCard({ skipped }: { skipped: SkippedTable[] }) {
  const t = useT();
  const groups = group(skipped);

  return (
    <Card>
      <CardHead title={t("crmImport.skipped.title")} sub={t("crmImport.skipped.desc")} />

      <div className="px-6 pb-6 pt-1">
        {groups.length === 0 ? (
          <p className="crm-sub text-[12.5px]">{t("crmImport.skipped.empty")}</p>
        ) : (
          <ul className="space-y-3">
            {groups.map((bucket) => {
              const why = reasonWhy(t, bucket.reason);
              return (
                <li key={bucket.reason} className="rounded-[20px] bg-[var(--crm-sunk)] px-4 py-3.5">
                  <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                    <PackageOpen className="size-4 shrink-0 self-center text-muted-foreground" />
                    <span className="text-[13px] font-bold">{reasonLabel(t, bucket.reason)}</span>
                    <span className="text-[12px] tabular-nums text-muted-foreground">
                      {fmtNum(bucket.rows)}
                    </span>
                  </div>
                  {why ? <p className="mt-1.5 text-[12.5px] leading-relaxed">{why}</p> : null}
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                    {bucket.tables.map((table) => (
                      <span key={table.table} className="text-[11.5px] text-muted-foreground">
                        <code className="font-semibold">{table.table}</code>{" "}
                        <span className="tabular-nums">{fmtNum(table.rows)}</span>
                      </span>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}
